import * as React from 'react'
import Button from '@mui/material/Button'
import { Alert, Box, Checkbox, Collapse, Dialog, DialogActions, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Select, SelectChangeEvent, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { appdata, Material, DataJSON, MemberJSON, AMember, MaterialJSON, AssemblyJSON, WallType, featherVersion } from '../internalModules'
import { boxStyle, dialogTypographyStyle } from '../Graphics'
import { getDependentGuids } from '../utility'

type Props = {
  isOpen: boolean
  closeDialog: Function
  updateApp: Function
}

export default function UIContentImportDialog({isOpen, closeDialog, updateApp}: Props) {

  const initFileName = 'choose file...'
  const initChecked: string[] = []
  const initDependents: string[] = []
  const initMats: Material[] = []
  const initMembers: AMember[] = []
  const initAssemblies: WallType[] = []

  const [fileName, setFileName] = React.useState(initFileName)
  const [checked, setChecked] = React.useState(initChecked)
  const [dependents, setDependents] = React.useState(initDependents)
  const [mats, setMats] = React.useState(initMats)
  const [members, setMembers] = React.useState(initMembers)
  const [assemblies, setAssemblies] = React.useState(initAssemblies)
  

  const resetDialog = () => {
    setFileName(initFileName)
    setChecked(initChecked)
    setDependents(initDependents)
    setMats(initMats)
    setMembers(initMembers)
    setAssemblies(initAssemblies)
  }

  const handleClickOk = () => {
    for(const mat of mats){
      if(checked.includes(mat.guid)){
        appdata.createMaterial(mat)
      }
    }
    for(const member of members){
      if(checked.includes(member.guid)){
        appdata.createMember(member)
      }
    }
    for(const assembly of assemblies){
      if(checked.includes(assembly.guid)){
        appdata.createWallType(assembly)
        assembly.calculateGwpSF()
        assembly.regenGeometry()
      }
    }
    updateApp()
    resetDialog()
    closeDialog()
  }

  const handleClickCancel = () => {
    resetDialog()
    closeDialog()
  }

  const handleToggle = (guid: string) => () => {
    const {newChecked, newDependents} = getNewCheckedAndDependents([guid])
    setChecked(newChecked)
    setDependents(newDependents)
  }

  function getNewCheckedAndDependents(guids: string[]): {newChecked: string[], newDependents: string[]} {
    let newChecked = [...checked]
    let newDependents = [...dependents]
    for(const guid of guids){
      const index = newChecked.findIndex(x=> x === guid)
      // when checking an item
      if (index === -1) {
        newChecked.push(guid)
        for(const assembly of assemblies){
          if(assembly.guid === guid){
            const depGuids = getDependentGuids(assembly)
            newChecked = newChecked.concat(depGuids)
            newDependents = newDependents.concat(depGuids)
          }
        }
        for(const member of members){
          //const depMemberGuids = Array.from(members.map(x=>x.guid))
          if(member.guid === guid || newDependents.includes(member.guid)){
            const depGuids = getDependentGuids(member)
            newChecked = newChecked.concat(depGuids)
            newDependents = newDependents.concat(depGuids)
          }
        }
      }
  
      // when unchecking an item
      else {
        newChecked.splice(index, 1)
        // remove dependents of assemblies
        for(const assembly of assemblies){
          if(assembly.guid === guid){
            const depGuids = getDependentGuids(assembly)
            for(const id of depGuids){
              const i = newDependents.indexOf(id)
              if(i > -1){
                newDependents.splice(i, 1)
              }
            }
          }
        }
        // remove dependents of members
        for(const member of members){
          if(member.guid === guid ){
            const depGuids = getDependentGuids(member)
            for(const id of depGuids){
              const i = newDependents.indexOf(id)
              if(i > -1){
                newDependents.splice(i, 1)
              }
            }
          }
        }
      }
    }
    
    // eliminate duplicate guids
    const uniqueDependents = [...new Set(newDependents)]
    const uniqueChecked = [...new Set(newChecked)]
    return {newChecked: uniqueChecked, newDependents: uniqueDependents}
  }

  function handleChooseFile(event: React.ChangeEvent) {
    resetDialog()
    const inputElement = event.target as HTMLInputElement
    if (inputElement.files) {
      const file = inputElement.files[0]
      const reader = new FileReader()
      reader.onload = loadJson
      reader.readAsText(file)
      setFileName(file.name)
    }
  }

  function loadJson(e: ProgressEvent<FileReader>) {
    const jsonText = e?.target?.result as string
    if (jsonText && jsonText.length > 0) {
      const data = JSON.parse(jsonText) as DataJSON
      if(data){
        const matData = data.materials as MaterialJSON[]
        if(matData){
          const newMats = matData.map(x=>appdata.materialFromJSON(x)).filter(x=> !x.isEmpty())
          setMats(newMats)
        }
        const memberData = data.members as MemberJSON[]
        if(memberData){
          const newMembers = memberData.map(x=>appdata.memberFromJSON(x))
          setMembers(newMembers)
        }
        const assemblyData = data.assemblies as AssemblyJSON[]
        if(assemblyData){
          const newAssemblies = assemblyData.map(x=>appdata.assemblyFromJSON(x))
          setAssemblies(newAssemblies)
        }
      }
      else{
        alert(`Json file does not match data schema for feather ${featherVersion}.`)
      }
    }
    else {
      alert('Cannot read file.')
    }
  }

  function listItems(itemKind: 'members'|'materials'|'assemblies') {
    let items: AMember[]|Material[]|WallType[] = []
    switch (itemKind) {
      case 'members':
        items = members
        break
      case 'materials':
        items = mats
        break
      case 'assemblies':
        items = assemblies
        break
      default:
        break
    }
    if(items.length > 0){
      return (
        <Paper elevation={0} sx={{maxHeight: '60vh', minHeight: '60vh', overflowY: 'auto', margin:0.25, border: '1px solid #189AD3'}}>
            {items.map((i) => {
              const labelId = `checkbox-list-label-${i.guid}`
              return (
                <ListItem key={i.guid} divider dense sx={{pl: 0, py: 0, pr: 0}}>
                  <Tooltip arrow followCursor title={i.name} enterDelay={500} enterNextDelay={300}>
                    <ListItemButton role={undefined} onClick={handleToggle(i.guid)}
                      disabled={dependents.includes(i.guid)} disableRipple sx={{maxHeight: 34}}
                    >
                      <ListItemIcon sx={{minWidth:30}}>
                        <Checkbox
                          edge="start"
                          checked={checked.indexOf(i.guid) !== -1}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText id={labelId} primary={i.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          style: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }
                      }}/>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              )
            })}
        </Paper>
      )
    }
    return(<></>)
  }

  function handleClickAll(event: React.SyntheticEvent){
    const target = event.target as HTMLElement
    let ids: string[] = []
    switch (target.id) {
      case 'assemblies':
        ids = assemblies.map(x=>x.guid)
        break
      case 'members':
        ids = members.map(x=>x.guid)
        break
      case 'mats':
        ids = mats.map(x=>x.guid)
        break
      default:
        break
    }
    const idsToCheck = Array.from(ids.filter(x=>!checked.includes(x)))
    const {newChecked, newDependents} = getNewCheckedAndDependents(idsToCheck)
    setChecked(newChecked)
    setDependents(newDependents)
  }

  function handleClickNone(event: React.SyntheticEvent){
    const target = event.target as HTMLElement
    let ids: string[] = []
    switch (target.id) {
      case 'assemblies':
        ids = assemblies.map(x=>x.guid)
        break
      case 'members':
        ids = members.map(x=>x.guid)
        break
      case 'mats':
        ids = mats.map(x=>x.guid)
        break
      default:
        break
    }
    const idsToUncheck = Array.from(ids.filter(x=> checked.includes(x) && !dependents.includes(x)))
    const {newChecked, newDependents} = getNewCheckedAndDependents(idsToUncheck)
    setChecked(newChecked)
    setDependents(newDependents)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClickCancel}
      maxWidth='lg'
      fullWidth
      scroll='body'
      PaperProps={{ sx: { marginTop: '3%', verticalAlign: "top"} }}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title" fontSize={30} sx={{pl:4}}>
        Import Content:
      </DialogTitle>
      <Box sx={boxStyle}>
        <Stack spacing={2} direction="row">
          <Typography color='secondary'>From file: </Typography>
          <Button variant='outlined' component="label" sx={{marginBottom: 2, marginLeft: 2, height: 25, textTransform: 'lowercase'}}>
            <i>{fileName}</i>
            <input hidden accept=".json" type="file" onChange={handleChooseFile}/>
          </Button>
        </Stack>

        <Grid container direction="row"  justifyContent="center"  alignItems="stretch" marginTop={2}
          visibility={mats.length > 0 ? 'visible' : 'hidden'}
        >
          <Grid item xs={4}>
            <Typography color='secondary' marginLeft={1} > <b>Assemblies:</b></Typography>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickAll} id='assemblies'>Check all</Button>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickNone} id='assemblies'>Check none</Button>
            {listItems('assemblies')}
          </Grid>
          <Grid item xs={4}>
            <Typography color='secondary' marginLeft={1}>Members: </Typography>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickAll} id='members'>Check all</Button>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickNone} id='members'>Check none</Button>
            {listItems('members')}
          </Grid>
          <Grid item xs={4}>
            <Typography color='secondary' marginLeft={1}>Materials: </Typography>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickAll} id='mats'>Check all</Button>
            <Button variant='outlined' size='small' sx={{m:0.25}} onClick={handleClickNone} id='mats'>Check none</Button>
            {listItems('materials')}
          </Grid>
        </Grid>
      </Box>

      <DialogActions>
        <Button variant='contained' onClick={handleClickOk} sx={{marginBottom: 2}}>
          Ok
        </Button>
        <Button variant='outlined' onClick={handleClickCancel} sx={{marginRight: 3, marginBottom: 2}}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}